import {
  deleteChatSessionsByChatSessionIdMutation,
  getChatSessionsByChatSessionIdQueryKey,
  getChatSessionsQueryKey,
  patchChatSessionsByChatSessionIdMutation,
} from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useChatSessionMutations = () => {
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: getChatSessionsQueryKey() });

  const patchMutation = useMutation({
    ...patchChatSessionsByChatSessionIdMutation(),
    onError: showErrorToast,
    onSettled: (_data, _error, variables) => {
      invalidateList();
      // Also refresh the single-session query so an open chat reflects the
      // change (e.g. the share toggle in the chat header).
      const chatSessionId = variables?.path?.chatSessionId;
      if (chatSessionId) {
        queryClient.invalidateQueries({
          queryKey: getChatSessionsByChatSessionIdQueryKey({ path: { chatSessionId } }),
        });
      }
    },
  });

  const deleteMutation = useMutation({
    ...deleteChatSessionsByChatSessionIdMutation(),
    onError: showErrorToast,
    onSettled: invalidateList,
  });

  return {
    toggleStar: (chatSessionId: string, nextStarred: boolean) =>
      patchMutation.mutate({ path: { chatSessionId }, body: { isStarred: nextStarred } }),
    toggleShare: (chatSessionId: string, nextShared: boolean) =>
      patchMutation.mutate({ path: { chatSessionId }, body: { isShared: nextShared } }),
    renameSession: (chatSessionId: string, title: string) =>
      patchMutation.mutate({ path: { chatSessionId }, body: { title } }),
    deleteSession: (chatSessionId: string) => deleteMutation.mutate({ path: { chatSessionId } }),
  };
};

export default useChatSessionMutations;
